import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { ApiProblemDetails } from '@tabify/models/types';
import { NotificationService } from '../services/notification.service';

/**
 * Check if the error should be silently handled (no notification shown)
 */
function shouldSkipNotification(req: { url: string }, error: HttpErrorResponse): boolean {
  // Skip 404 on transactions/tables endpoint (no open transaction for table is expected)
  if (error.status === 404 && /\/api\/v1\/transactions\/tables\/[^/]+$/.test(req.url)) {
    return true;
  }

  // Skip 404 on wallet/config endpoint (wallet config not found is expected during dev)
  if (error.status === 404 && /\/api\/v1\/wallet\/config\/[^/]+$/.test(req.url)) {
    return true;
  }

  // Skip wallet-result callbacks — wallet-payment-result.component handles
  // these failures itself and already renders the cancelled / declined UI.
  // The interceptor toast would be redundant (and was misleading when the
  // backend returned a 400 like "already processed").
  if (
    /\/api\/v1\/transactions\/[^/]+\/payments\/[^/]+\/(cancelled|declined)$/.test(
      req.url,
    )
  ) {
    return true;
  }

  // Skip the feedback endpoint — post-payment-feedback.component owns every
  // outcome itself: a 404 on load is the normal "not rated yet" case, a 409 is
  // shown as a friendly "already submitted", and submit failures already get
  // their own toast. The interceptor would either invent an error where there
  // is none, or double up on one the component just showed.
  if (
    /\/api\/v1\/transactions\/[^/]+\/payments\/[^/]+\/feedback$/.test(req.url)
  ) {
    return true;
  }

  return false;
}

/**
 * HTTP Error Interceptor
 * Intercepts all HTTP errors and displays user-friendly error messages
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notification = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unexpected error occurred';

      if (error.error) {
        // Check if response is RFC 9110 Problem Details format
        if (isProblemDetails(error.error)) {
          errorMessage = formatProblemDetails(error.error);
        }
        // Check if response has a simple message property
        else if (typeof error.error === 'object' && error.error.message) {
          errorMessage = error.error.message;
        }
        // Backend's legacy `{ success: false, error: "..." }` envelope —
        // e.g. /payments/{id}/cancelled returns this shape. The body's
        // `error` field is the canonical customer-facing message.
        else if (
          typeof error.error === 'object' &&
          typeof error.error.error === 'string'
        ) {
          errorMessage = error.error.error;
        }
        // Check if response is a string
        else if (typeof error.error === 'string') {
          errorMessage = error.error;
        }
        // Fallback when the body is empty/unparseable. Drop statusText —
        // HTTP/2 strips the reason phrase and Angular defaults it to "OK",
        // which is misleading on 4xx/5xx responses.
        else {
          errorMessage = `Request failed with status ${error.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Display error to user (unless it should be skipped)
      if (!shouldSkipNotification(req, error)) {
        notification.showError(errorMessage);
      }

      // Log to console for debugging
      console.error('HTTP Error:', {
        url: req.url,
        status: error.status,
        error: error.error,
        message: errorMessage,
      });

      // Re-throw error for component-level handling if needed
      return throwError(() => error);
    })
  );
};

/**
 * Type guard to check if error response is Problem Details format
 */
function isProblemDetails(error: unknown): error is ApiProblemDetails {
  return (
    typeof error === 'object' &&
    error !== null &&
    'title' in error &&
    'status' in error
  );
}

/**
 * Format Problem Details error into user-friendly message
 */
function formatProblemDetails(problemDetails: ApiProblemDetails): string {
  const messages: string[] = [];

  // Add title/detail
  if (problemDetails.detail) {
    messages.push(problemDetails.detail);
  } else if (problemDetails.title) {
    messages.push(problemDetails.title);
  }

  // Add validation errors if present
  if (problemDetails.errors) {
    const validationMessages = Object.entries(problemDetails.errors)
      .flatMap(([field, errors]) => {
        // Format: "FieldName: Error message"
        return errors.map((error) => `${field}: ${error}`);
      });

    messages.push(...validationMessages);
  }

  return messages.join('\n') || 'Validation error occurred';
}
